"use client";

import { useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  Calendar,
  ChevronDown,
  ChevronUp,
  Coins,
  Heart,
  Loader2,
  MapPin,
  Settings2,
  Shield,
  User,
} from "lucide-react";

import { QuoteCompareIcon } from "@/app/_module/components/icons/QuoteCompareIcon";
import { Button } from "@/app/_module/components/ui/button";
import { Input } from "@/app/_module/components/ui/input";
import { useGenerateQuotes } from "@/app/_module/hooks/useQuoteService";
import type { QuoteRequest } from "@/app/_module/services/quoteService";
import type { LeadWithPolicies } from "../types/lead-with-policies-type";
import {
  parseLeadHeight,
  parseLeadWeight,
} from "@/app/(authenticated)/quote-enroll/_modules/components/QuoteForm";

interface CompulifeQuoteFormProps {
  leadId?: string;
  lead?: LeadWithPolicies;
}

const FACE_CHIPS = [100_000, 250_000, 500_000, 750_000, 1_000_000];
const TERM_LENGTHS = [10, 15, 20, 25, 30];

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
];

/**
 * Compulife-flavored sibling of the Techficient QuoteForm. Same layout +
 * micro-interactions so agents who learned one know the other immediately.
 *
 * Differences vs. the Techficient form (because Compulife's payload shape
 * is different):
 *   - Gender enum is `Male`/`Female` (not `M`/`F`)
 *   - Product type includes Whole Life / Universal Life (Compulife filters
 *     the carriers by product on the backend, not us)
 *   - Health class is an enum (Preferred Plus / Preferred / Regular Plus /
 *     Regular) rather than the open PHC checklist
 *   - Tobacco is a tri-state (Yes / No / Former) rather than boolean
 *
 * Results render via the existing `useQuotes(leadId)` query on the parent —
 * Compulife persists quotes on the backend, so after submit we invalidate
 * and the Quote Results table below re-renders with the new data.
 */
export function CompulifeQuoteForm({ leadId, lead }: CompulifeQuoteFormProps) {
  // Pre-fill from the lead row. Compulife uses snake_case on a couple of
  // fields, so we coalesce camelCase first then snake.
  const presetName =
    lead?.name ||
    [lead?.first_name, lead?.last_name].filter(Boolean).join(" ").trim();
  const presetDob = lead?.dob || (lead as any)?.birthday || "";
  const presetGender: "Male" | "Female" | "" = ((): "Male" | "Female" | "" => {
    const g = (lead as any)?.gender;
    if (!g) return "";
    if (typeof g !== "string") return "";
    const t = g.trim().toUpperCase();
    if (t.startsWith("F")) return "Female";
    if (t.startsWith("M")) return "Male";
    return "";
  })();
  const presetState = (lead as any)?.state || "";
  const presetZip = (lead as any)?.zipCode || (lead as any)?.zip_code || "";
  // height/weight are VARCHAR on the lead row (`5'10"`, "175") — parse both
  // shapes; numeric-only checks silently dropped every string value.
  const presetHeightInches = parseLeadHeight((lead as any)?.height);
  const presetWeightLbs = parseLeadWeight((lead as any)?.weight);

  // ─── form state ───
  const [productType, setProductType] = useState<
    "Term Life" | "Whole Life" | "Universal Life"
  >("Term Life");
  const [coverageAmount, setCoverageAmount] = useState<number | "">(250_000);
  const [termYears, setTermYears] = useState<number>(20);
  const [name, setName] = useState(presetName);
  const [dob, setDob] = useState<string>(normalizeDate(presetDob));
  const [gender, setGender] = useState<"Male" | "Female" | "">(presetGender);
  const [state, setState] = useState<string>(presetState);
  const [zipCode, setZipCode] = useState<string>(presetZip);
  const [heightFt, setHeightFt] = useState<number | "">(
    presetHeightInches != null ? Math.floor(presetHeightInches / 12) : "",
  );
  const [heightIn, setHeightIn] = useState<number | "">(
    presetHeightInches != null ? presetHeightInches % 12 : "",
  );
  const [weightLbs, setWeightLbs] = useState<number | "">(
    presetWeightLbs ?? "",
  );
  const [smokerStatus, setSmokerStatus] = useState<"Yes" | "No" | "Former" | "">(
    "",
  );
  const [healthClass, setHealthClass] = useState<
    "Preferred Plus" | "Preferred" | "Regular Plus" | "Regular" | ""
  >("");
  const [paymentFrequency, setPaymentFrequency] = useState<
    "Monthly" | "Quarterly" | "Semi-Annual" | "Annual"
  >("Monthly");
  const [healthOpen, setHealthOpen] = useState(
    presetHeightInches != null || presetWeightLbs != null,
  );
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const generateMut = useGenerateQuotes();

  // Re-seed when the lead changes — useful when LeadsQuote is reused across
  // navigation without unmounting.
  useEffect(() => {
    if (presetName) setName(presetName);
    if (presetDob) setDob(normalizeDate(presetDob));
    if (presetGender) setGender(presetGender);
    if (presetState) setState(presetState);
    if (presetZip) setZipCode(presetZip);
    if (presetHeightInches != null) {
      setHeightFt(Math.floor(presetHeightInches / 12));
      setHeightIn(presetHeightInches % 12);
    }
    if (presetWeightLbs != null) setWeightLbs(presetWeightLbs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead?.id]);

  // ─── validation ───
  const missing = useMemo(() => {
    const m: string[] = [];
    if (!coverageAmount || Number(coverageAmount) <= 0) m.push("Coverage amount");
    if (!dob) m.push("Date of birth");
    if (!gender) m.push("Gender");
    if (!state) m.push("State");
    if (!leadId) m.push("Lead ID");
    return m;
  }, [coverageAmount, dob, gender, state, leadId]);
  const canSubmit = missing.length === 0 && !generateMut.isPending;

  // ─── submit ───
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !leadId) return;

    const totalInches = (Number(heightFt) || 0) * 12 + (Number(heightIn) || 0);
    const heightStr = totalInches > 0 ? `${heightFt}'${heightIn}"` : "";
    const weightStr = weightLbs ? `${weightLbs} lbs` : "";

    const payload: QuoteRequest = {
      fullName: name || "Lead",
      dob,
      gender: gender as "Male" | "Female",
      height: heightStr,
      weight: weightStr,
      coverageAmount: String(coverageAmount),
      state,
      zipCode,
      productType,
      paymentFrequency,
      ...(smokerStatus ? { smokerStatus } : {}),
      ...(healthClass ? { healthClass } : {}),
    };

    try {
      await generateMut.mutateAsync({ leadId, quoteData: payload });
      // Quote results show up in the Quote Results table below — the hook
      // invalidates the useQuotes(leadId) query on success.
      scrollToResults();
    } catch {
      // QuoteService already raises a toast — nothing else for us to do.
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {/* Coverage type */}
      <Section icon={Shield} label="Coverage type">
        <div className="grid grid-cols-3 gap-2">
          <TypePill
            active={productType === "Term Life"}
            label="Term"
            hint="Affordable · expires"
            onClick={() => setProductType("Term Life")}
          />
          <TypePill
            active={productType === "Whole Life"}
            label="Whole Life"
            hint="Permanent · builds cash"
            onClick={() => setProductType("Whole Life")}
          />
          <TypePill
            active={productType === "Universal Life"}
            label="Universal"
            hint="Flexible permanent"
            onClick={() => setProductType("Universal Life")}
          />
        </div>
      </Section>

      {/* Amount + term */}
      <Section icon={Coins} label="Coverage amount">
        <div className="flex flex-wrap gap-2 mb-2">
          {FACE_CHIPS.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCoverageAmount(c)}
              className={`px-3 py-1 rounded-full border text-xs font-medium transition ${
                Number(coverageAmount) === c
                  ? "bg-primary text-white border-primary"
                  : "bg-white text-gray-700 border-gray-200 hover:border-gray-300"
              }`}
            >
              {c >= 1_000_000 ? `$${c / 1_000_000}M` : `$${c / 1_000}K`}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">$</span>
          <Input
            type="number"
            inputMode="numeric"
            value={coverageAmount}
            onChange={(e) =>
              setCoverageAmount(e.target.value === "" ? "" : Number(e.target.value))
            }
            className="h-9"
            min={0}
            step={1000}
          />
          {productType === "Term Life" && (
            <select
              value={termYears}
              onChange={(e) => setTermYears(Number(e.target.value))}
              className="h-9 rounded-md border border-gray-200 bg-white px-2 text-sm focus:border-primary focus:outline-none"
            >
              {TERM_LENGTHS.map((y) => (
                <option key={y} value={y}>
                  {y} years
                </option>
              ))}
            </select>
          )}
        </div>
      </Section>

      {/* Applicant */}
      <Section icon={User} label="About the applicant">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Full name">
            <Input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="h-9"
              placeholder="—"
            />
          </Field>
          <Field label="Date of birth">
            <div className="relative">
              <Calendar className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
              <Input
                type="date"
                value={dob}
                onChange={(e) => setDob(e.target.value)}
                className="h-9 pl-7"
                max={new Date().toISOString().slice(0, 10)}
              />
            </div>
          </Field>
        </div>
        <div className="mt-3">
          <Field label="Gender">
            <div className="grid grid-cols-2 gap-2 max-w-xs">
              <GenderPill
                active={gender === "Male"}
                label="Male"
                onClick={() => setGender("Male")}
              />
              <GenderPill
                active={gender === "Female"}
                label="Female"
                onClick={() => setGender("Female")}
              />
            </div>
          </Field>
        </div>
      </Section>

      {/* Location */}
      <Section icon={MapPin} label="Location">
        <div className="grid grid-cols-[1fr_140px] gap-3">
          <Field label="State">
            <select
              value={state}
              onChange={(e) => setState(e.target.value)}
              className="h-9 w-full rounded-md border border-gray-200 bg-white px-2 text-sm focus:border-primary focus:outline-none"
            >
              <option value="">Select…</option>
              {US_STATES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </Field>
          <Field label="ZIP">
            <Input
              type="text"
              inputMode="numeric"
              pattern="\d{5}"
              maxLength={5}
              value={zipCode}
              onChange={(e) => setZipCode(e.target.value.replace(/\D/g, ""))}
              className="h-9"
              placeholder="—"
            />
          </Field>
        </div>
      </Section>

      {/* Health */}
      <button
        type="button"
        onClick={() => setHealthOpen((v) => !v)}
        className="mt-4 w-full flex items-center justify-between rounded-md border border-dashed border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
      >
        <span className="inline-flex items-center gap-2">
          <Heart className="h-3.5 w-3.5 text-rose-400" />
          Health
          <span className="text-[10px] text-gray-400 font-normal">
            (improves rates)
          </span>
        </span>
        {healthOpen ? (
          <ChevronUp className="h-3.5 w-3.5 text-gray-400" />
        ) : (
          <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
        )}
      </button>

      {healthOpen && (
        <div className="mt-3 rounded-md bg-gray-50/60 p-3 space-y-3 border border-gray-100">
          <div className="grid grid-cols-3 gap-3">
            <Field label="Height (ft)">
              <Input
                type="number"
                inputMode="numeric"
                value={heightFt}
                min={3}
                max={8}
                onChange={(e) =>
                  setHeightFt(e.target.value === "" ? "" : Number(e.target.value))
                }
                className="h-9"
              />
            </Field>
            <Field label="Height (in)">
              <Input
                type="number"
                inputMode="numeric"
                value={heightIn}
                min={0}
                max={11}
                onChange={(e) =>
                  setHeightIn(e.target.value === "" ? "" : Number(e.target.value))
                }
                className="h-9"
              />
            </Field>
            <Field label="Weight (lbs)">
              <Input
                type="number"
                inputMode="numeric"
                value={weightLbs}
                min={50}
                max={600}
                onChange={(e) =>
                  setWeightLbs(
                    e.target.value === "" ? "" : Number(e.target.value),
                  )
                }
                className="h-9"
              />
            </Field>
          </div>
          <Field label="Tobacco use">
            <div className="grid grid-cols-3 gap-2 max-w-md">
              <TogglePill
                active={smokerStatus === "No"}
                label="No"
                onClick={() => setSmokerStatus("No")}
              />
              <TogglePill
                active={smokerStatus === "Former"}
                label="Former (12+ mo)"
                onClick={() => setSmokerStatus("Former")}
              />
              <TogglePill
                active={smokerStatus === "Yes"}
                label="Yes"
                onClick={() => setSmokerStatus("Yes")}
              />
            </div>
          </Field>
          <Field label="Health class (optional override)">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {(
                [
                  "Preferred Plus",
                  "Preferred",
                  "Regular Plus",
                  "Regular",
                ] as const
              ).map((h) => (
                <TogglePill
                  key={h}
                  active={healthClass === h}
                  label={h}
                  onClick={() => setHealthClass(h)}
                />
              ))}
            </div>
          </Field>
        </div>
      )}

      {/* Advanced */}
      <button
        type="button"
        onClick={() => setAdvancedOpen((v) => !v)}
        className="mt-3 w-full flex items-center justify-between rounded-md border border-dashed border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
      >
        <span className="inline-flex items-center gap-2">
          <Settings2 className="h-3.5 w-3.5 text-gray-400" />
          Advanced
          <span className="text-[10px] text-gray-400 font-normal">
            (payment frequency)
          </span>
        </span>
        {advancedOpen ? (
          <ChevronUp className="h-3.5 w-3.5 text-gray-400" />
        ) : (
          <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
        )}
      </button>

      {advancedOpen && (
        <div className="mt-3 rounded-md bg-gray-50/60 p-3 space-y-3 border border-gray-100">
          <Field label="Payment frequency">
            <div className="grid grid-cols-4 gap-1.5">
              {(["Monthly", "Quarterly", "Semi-Annual", "Annual"] as const).map(
                (m) => (
                  <TogglePill
                    key={m}
                    active={paymentFrequency === m}
                    label={m}
                    onClick={() => setPaymentFrequency(m)}
                  />
                ),
              )}
            </div>
          </Field>
        </div>
      )}

      {/* Submit */}
      <div className="mt-5 flex items-center justify-between gap-3">
        {missing.length > 0 ? (
          <p className="text-[11px] text-amber-600 inline-flex items-center gap-1">
            <AlertCircle className="h-3 w-3" />
            Still needed: {missing.join(", ")}
          </p>
        ) : (
          <span />
        )}
        <Button type="submit" disabled={!canSubmit} className="min-w-[180px]">
          {generateMut.isPending ? (
            <>
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              Running…
            </>
          ) : (
            <>
              {/* Vendor name never appears in UI copy — "Compulife" is a supplier
                  detail, not something agents or their clients should ever read. */}
              <QuoteCompareIcon className="h-3.5 w-3.5 mr-1.5" />
              Run instant quote
            </>
          )}
        </Button>
      </div>

      {generateMut.isError && (
        <div className="mt-3 flex items-start gap-2 rounded-md bg-rose-50 border border-rose-200 px-3 py-2 text-xs text-rose-700">
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span>
            {(generateMut.error as any)?.message ||
              "The quote service didn't respond — try again."}
          </span>
        </div>
      )}
    </form>
  );
}

// ─────────────────────────── helpers ───────────────────────────

function normalizeDate(raw: string | null | undefined): string {
  if (!raw) return "";
  const isoMatch = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoMatch) return isoMatch[1];
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

function scrollToResults() {
  requestAnimationFrame(() => {
    document
      .getElementById("quote-results-anchor")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  });
}

function Section({
  icon: Icon,
  label,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mt-3">
      <p className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold mb-2 inline-flex items-center gap-1.5">
        <Icon className="h-3 w-3 text-gray-400" />
        {label}
      </p>
      {children}
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-[10px] text-gray-500 mb-0.5">{label}</span>
      {children}
    </label>
  );
}

function TypePill({
  active,
  label,
  hint,
  onClick,
}: {
  active: boolean;
  label: string;
  hint: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left rounded-lg border px-3 py-2.5 transition ${
        active
          ? "border-primary bg-primary/5 ring-1 ring-primary/40"
          : "border-gray-200 bg-white hover:border-gray-300"
      }`}
    >
      <p
        className={`text-sm font-semibold ${
          active ? "text-primary" : "text-gray-900"
        }`}
      >
        {label}
      </p>
      <p className="text-[10px] text-gray-500 mt-0.5">{hint}</p>
    </button>
  );
}

function GenderPill({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`h-9 rounded-md border text-sm font-medium transition ${
        active
          ? "border-primary bg-primary/5 text-primary"
          : "border-gray-200 bg-white text-gray-700 hover:border-gray-300"
      }`}
    >
      {label}
    </button>
  );
}

function TogglePill({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`h-9 rounded-md border text-xs font-medium transition px-2 ${
        active
          ? "border-primary bg-primary/5 text-primary"
          : "border-gray-200 bg-white text-gray-700 hover:border-gray-300"
      }`}
    >
      {label}
    </button>
  );
}

export default CompulifeQuoteForm;
